import { LiveData, Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import { sortTaskItems } from '../types';
import type { TaskItem } from '../types';
import type { MyTasksStore } from '../stores/my-tasks';

/**
 * Read-only aggregation of all tasks that mention the current user across the
 * workspace. See `MyTasksStore` for how the scan works.
 */
export class MyTasksService extends Service {
  /** current account id, `null` for local workspaces (no mentions possible) */
  readonly userId$: LiveData<string | null>;

  /** all tasks, sorted by project / update time */
  readonly tasks$: LiveData<TaskItem[]>;

  /** true while the workspace is still being scanned */
  readonly scanning$: LiveData<boolean>;

  /** number of tasks that are not done yet */
  readonly openCount$: LiveData<number>;

  constructor(
    private readonly store: MyTasksStore,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();

    this.userId$ = LiveData.computed(get => {
      const server = get(this.workspaceServerService.server$);
      const account = server ? get(server.account$) : null;
      return account?.id ?? null;
    });

    this.tasks$ = LiveData.computed(get => {
      const tasksByDoc = get(this.store.tasksMap$);
      const all: TaskItem[] = [];
      for (const tasks of tasksByDoc.values()) {
        all.push(...tasks);
      }
      return sortTaskItems(all);
    });

    this.scanning$ = this.store.scanning$;

    this.openCount$ = this.tasks$.map(tasks => tasks.filter(t => !t.done).length);
  }
}
